import { useMemo, useState } from 'react';
import { useReservations } from '@/hooks/api-hooks';
import { Badge, Loader, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import ReservationsList from './reservations-list';
import ReservationsMoneySummary from './reservation/reservations-money-summary';
import { IconCalendar } from '@tabler/icons-react';
import type { Business } from '@turizi/shared';
import { PAYMENT_STATUS_COLORS, RESERVATION_PAYMENT_STATUS_ALL } from '@turizi/shared';
import SearchInput from './search-input';
import { filterByMultiWordSearch } from '@/utils/search-utils';
import { ReservationToString } from '@/lib/models-utils';

interface Props {
    business?: Business;
    client_id_business?: number | string;
    start_day?: string;
    end_day?: string;
}

export default function BusinessReservationsAsClientTab({
    business,
    client_id_business,
    start_day,
    end_day,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<[string, string]>([start_day || '', end_day || '']);
    const clientId = client_id_business ?? business?.id;

    const effectiveStartDay = dateRange[0] || start_day;
    const effectiveEndDay = dateRange[1] || end_day;

    const {
        data: reservationsData,
        isLoading,
    } = useReservations({
        client_id_business: clientId,
        start_day: effectiveStartDay,
        end_day: effectiveEndDay,
    }, { enabled: !!clientId });

    const reservations = reservationsData?.data || [];

    const paymentStatusOptions = useMemo(() => {
        return RESERVATION_PAYMENT_STATUS_ALL(t);
    }, [t]);

    const filteredReservations = useMemo(() => {
        let list = filterByMultiWordSearch(reservations, searchText, (reservation) => {
            return ReservationToString(reservation, t);
        });
        if (effectiveStartDay) {
            list = list.filter(r => r.start_day && r.start_day >= effectiveStartDay);
        }
        if (effectiveEndDay) {
            list = list.filter(r => r.start_day && r.start_day <= effectiveEndDay);
        }
        if (paymentStatus) {
            list = list.filter(r => {
                const status = r.payment_status || 'unpaid';
                if (paymentStatus === 'partially_paid') {
                    return status === 'partially_paid' || status === 'partial';
                }
                return status === paymentStatus;
            });
        }
        return list;
    }, [reservations, searchText, effectiveStartDay, effectiveEndDay, paymentStatus, t]);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2.5 mb-2 border-b border-slate-100 pb-2">
                <div className="p-1.5 bg-blue-50 rounded-lg text-blue-650 shrink-0">
                    <IconCalendar size={16} stroke={2} />
                </div>
                <div className="flex items-center gap-2 flex-1">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{t('reservations_as_client')}</h3>
                    {isLoading && <Loader size="xs" color="black" />}
                </div>
            </div>
            {!isLoading && (
                <>
                    <ReservationsMoneySummary
                        business={business}
                        reservations={reservations}
                        dateRange={dateRange}
                        onDateRangeChange={setDateRange}
                    />
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                        <div className="flex-1 w-full">
                            <SearchInput
                                onSearch={setSearchText}
                                placeholder={t('search')}
                            />
                        </div>
                        <Select
                            clearable
                            placeholder={t('payment_status')}
                            value={paymentStatus}
                            onChange={(val) => setPaymentStatus(val)}
                            data={paymentStatusOptions}
                            leftSection={
                                paymentStatus ? (
                                    <Badge
                                        circle
                                        color={PAYMENT_STATUS_COLORS[paymentStatus] || 'gray'}
                                        className="!w-2 !h-2 !p-0 ml-1"
                                    />
                                ) : undefined
                            }
                            renderOption={({ option }) => {
                                const color = PAYMENT_STATUS_COLORS[option.value] || 'gray';
                                return (
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            circle
                                            color={color}
                                            className="!w-2 !h-2 !p-0 shrink-0"
                                        />
                                        <span>{option.label}</span>
                                    </div>
                                );
                            }}
                            radius="xl"
                            size="sm"
                            className="w-full sm:w-48 shrink-0"
                        />
                    </div>
                    <ReservationsList
                        business={business}
                        reservations={filteredReservations}
                        displayOptions={{
                            showClientName: true,
                            showProvider: false,
                            showCoordinator: false,
                            editable: false,
                            groupByPaxs: true,
                            showPaxs: true,
                            showPrice: true,
                            showCost: false,
                            showPaymentStatus: true,
                            selectedAttrKeys: []
                        }}
                    />
                </>
            )}
        </div>
    );
}
